/**
 * billingSlice — état Redux pour le système de crédits Life
 * (cf. spec "Life — Adaptation du système d'offres", §4.2).
 *
 * Structure :
 *   wallet        : portefeuille courant (subscription_credits, purchased_credits, total)
 *   subscription  : plan courant + date de prochaine recharge
 *   packs         : catalogue (CreditStore)
 *   transactions  : 20 derniers mouvements
 *   insufficient  : payload reçu sur HTTP 402 → ouvre la modale "Crédits insuffisants"
 *   loading/error : flags génériques
 */
import { createSlice } from '@reduxjs/toolkit'

const initialState = {
  wallet: {
    subscriptionCredits: 0,
    purchasedCredits: 0,
    total: 0,
    lastRefillAt: null,
    planQuota: 0,
  },
  subscription: {
    plan: 'free_trial',
    currentPeriodEnd: null,
    cancelAtPeriodEnd: false,
  },
  packs: [],
  transactions: [],
  insufficient: null, // { required, available, missing, feature, tier } | null
  loading: false,
  error: null,
}

const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    setBillingLoading: (state, action) => {
      state.loading = !!action.payload
    },
    setBillingError: (state, action) => {
      state.error = action.payload || null
      state.loading = false
    },
    setWallet: (state, action) => {
      const data = action.payload || {}
      state.wallet = {
        subscriptionCredits: data.subscription_credits ?? 0,
        purchasedCredits: data.purchased_credits ?? 0,
        total: data.total ?? 0,
        lastRefillAt: data.last_refill_at ?? null,
        planQuota: data.plan_quota ?? 0,
      }
      if (Array.isArray(data.transactions)) {
        state.transactions = data.transactions
      }
      state.loading = false
      state.error = null
    },
    setTransactions: (state, action) => {
      state.transactions = Array.isArray(action.payload) ? action.payload : []
    },
    setSubscription: (state, action) => {
      const data = action.payload || {}
      state.subscription = {
        plan: data.plan ?? 'free_trial',
        currentPeriodEnd: data.current_period_end ?? null,
        cancelAtPeriodEnd: !!data.cancel_at_period_end,
      }
    },
    setPacks: (state, action) => {
      state.packs = Array.isArray(action.payload) ? action.payload : []
    },
    openInsufficient: (state, action) => {
      state.insufficient = action.payload || null
    },
    closeInsufficient: (state) => {
      state.insufficient = null
    },
    // Appelé après une consommation côté front (mise à jour optimiste du
    // solde, le wallet réel sera resync au prochain fetch).
    decrementWalletOptimistic: (state, action) => {
      const cost = Math.max(0, Number(action.payload) || 0)
      let remaining = cost
      const fromSub = Math.min(state.wallet.subscriptionCredits, remaining)
      state.wallet.subscriptionCredits -= fromSub
      remaining -= fromSub
      state.wallet.purchasedCredits = Math.max(
        0,
        state.wallet.purchasedCredits - remaining,
      )
      state.wallet.total =
        state.wallet.subscriptionCredits + state.wallet.purchasedCredits
    },
    resetBilling: () => initialState,
  },
})

export const {
  setBillingLoading,
  setBillingError,
  setWallet,
  setTransactions,
  setSubscription,
  setPacks,
  openInsufficient,
  closeInsufficient,
  decrementWalletOptimistic,
  resetBilling,
} = billingSlice.actions

export default billingSlice.reducer

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectWallet = (state) => state.billing?.wallet
export const selectWalletTotal = (state) => state.billing?.wallet?.total ?? 0
export const selectPacks = (state) => state.billing?.packs ?? []
export const selectTransactions = (state) => state.billing?.transactions ?? []
export const selectSubscription = (state) => state.billing?.subscription
export const selectInsufficient = (state) => state.billing?.insufficient

// Statut visuel du badge (cf. spec §4.3) :
// - vert  : > 20 % du quota
// - orange: 5–20 %
// - rouge : < 5 % ou 0
export const selectWalletStatus = (state) => {
  const wallet = state.billing?.wallet
  if (!wallet) return 'unknown'
  const quota = Math.max(1, wallet.planQuota || 1)
  const ratio = wallet.total / quota
  if (wallet.total <= 0) return 'empty'
  if (ratio < 0.05) return 'critical'
  if (ratio < 0.2) return 'low'
  return 'ok'
}
